// @flow
import React, {forwardRef, FunctionComponent, PropsWithChildren, useCallback, useReducer} from 'react';
import FlexStack from "../components/Common/flex-stack";
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Stack,
    Switch,
    TextField,
    Typography
} from "@mui/material";
import {Widget} from "../components/Common";
import {useFieldArray, useForm, UseFormRegister} from "react-hook-form";
import {FarmData} from "../api/dtos/breeder-update";
import PersonAddIcon from "@mui/icons-material/PersonAddAlt1";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import {useMutation} from "@tanstack/react-query";
import {InfoBox, WarningBox} from "../components/Common/alerts";
import AuthService from "../services/auth-service";
import {Info, Visibility, VisibilityOff} from "@mui/icons-material";
import FormHelperText from "@mui/material/FormHelperText";

type SignupData = FarmData & {
    password: string,
    passwordConfirmation: string,
};

const requiredFieldConfig = {required: {value: true, message: "Pflichtfeld"}}; //TODO get i18n to work

const FlexWidget = (props: PropsWithChildren<{ displayName: React.ReactNode }>) => (
    <Widget displayName={props.displayName}>
        <FlexStack>
            {props.children}
        </FlexStack>
    </Widget>
);

const useToggle = (initialValue: boolean) => useReducer(x => !x, initialValue);

const PasswordField = forwardRef<HTMLInputElement, React.ComponentProps<typeof TextField>>((props, ref) => {
    const [showPassword, toggleShowPassword] = useToggle(false);

    return (
        <TextField
            ref={ref}
            type={showPassword ? 'text' : 'password'}
            slotProps={{
                input: {
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                aria-label={showPassword ? 'hide the password' : 'display the password'}
                                onClick={toggleShowPassword}
                                edge="end"
                            >
                                {showPassword ? <VisibilityOff/> : <Visibility/>}
                            </IconButton>
                        </InputAdornment>
                    )
                },
            }}
            {...props}
        />
    );
});

type SignupConfirmation = {
    terms: boolean;
    b2b: boolean;
    privacy: boolean;
};

const SignupBreeder: FunctionComponent = () => {
    const {formState: {errors}, register, handleSubmit, control, watch} = useForm<SignupData & SignupConfirmation>({
        defaultValues: {
            owners: [
                { name: '' },
            ],
        },
    });

    const {
        fields: owners,
        append: appendOwner,
        remove: removeOwner
    } = useFieldArray({control, name: "owners"})
    const inTbcProgram = watch('inTbcProgram');

    const farmNumber =  watch('farmNumber');
    const vatNumber = watch('vatNumber');
    const password = watch('password');

    const addOwner = useCallback(() => appendOwner({name: ""}), [appendOwner]);

    const {mutate: signup, isPending} = useMutation({
        mutationKey: ['signup'],
        mutationFn: async (signupData: SignupData) => await AuthService.signUp({
            ...signupData,
            email: signupData.emailAddress,
            farm: {
                ...signupData,
                owners: signupData.owners.map(p => p.name).filter(Boolean),
            },
        }),
        onError: () => alert('Registrierung fehlgeschlagen. Bitte wende dich an den Administrator.'),
        onSuccess: async () => {
            alert('Registrierung erfolgreich. Du kannst dich nun einloggen.');
            await AuthService.doLogin();
        },
    });

    const registerRequired: UseFormRegister<SignupData & SignupConfirmation> = useCallback((fieldName, options?) => ({
        ...register(fieldName, {...requiredFieldConfig, ...options}),
        required: true,
    }), [register]);

    return (
        <form onSubmit={handleSubmit(form => signup(form))} noValidate>
            <FlexStack spacing={4}>
                <Box>
                    <Typography variant="h4">Registriere dich als Züchter bei Alpacuna Herds</Typography>
                </Box>

                <FlexWidget displayName="Benutzerdaten">
                    <TextField
                        {...registerRequired('emailAddress')}
                        label="E-Mailadresse"
                        error={!!errors.emailAddress}
                        helperText={errors.emailAddress?.message ?? ''}/>
                    <TextField
                        {...registerRequired('username')}
                        label="Benutzername"
                        error={!!errors.username}
                        helperText={errors.username?.message ?? ''}/>
                    <PasswordField
                        {...registerRequired('password')}
                        label="Passwort"
                        error={!!errors.password}
                        helperText={errors.password?.message ?? ''}
                    />
                    <PasswordField
                        {...registerRequired('passwordConfirmation', {
                            validate: value => {
                                if (password !== value) {
                                    return 'Passwörter stimmen nicht überein!'
                                }
                                return true;
                            }
                        })}
                        label="Passwort wiederholen"
                        error={!!errors.passwordConfirmation}
                        helperText={errors.passwordConfirmation?.message ?? ''}/>
                </FlexWidget>

                <FlexWidget displayName="Hofdaten">
                    <TextField
                        {...registerRequired('farmName')}
                        label="Hofname"
                        error={!!errors.farmName}
                        helperText={errors.farmName?.message ?? ''}/>
                    <TextField{...register('herdCode')} label="Herdkürzel"/>
                    <TextField label="Webseite" {...register("website")}/>
                    <TextField label="Telefonnummer" {...register("phone")}/>
                    <TextField label="Hofnummer" {...register("farmNumber")}/>
                    <TextField label="Steuernummer" {...register("vatNumber")}/>
                    <FormControlLabel control={<Switch {...register('inTbcProgram')}/>}
                                      label={
                                          <Stack direction="row" gap={1}>
                                              TBC-Programm
                                              {inTbcProgram ? (
                                                  <Stack direction="row" gap={0.5}>
                                                      <Info/>
                                                      Mein Hof nimmt an einem TBC-Testprogramm teil und gilt als TBC frei
                                                  </Stack>
                                              ) : ''}
                                          </Stack>
                                      }/>
                </FlexWidget>

                <FlexWidget displayName="Adresse">
                    <TextField
                        {...registerRequired('address.country')}
                        label="Land"
                        error={!!errors.address?.country}
                        helperText={errors.address?.country?.message ?? ''}/>
                    <TextField
                        {...registerRequired('address.state')}
                        label="Bundesland"
                        error={!!errors.address?.state}
                        helperText={errors.address?.state?.message ?? ''}/>
                    <TextField
                        {...registerRequired('address.zipCode')}
                        label="PLZ"
                        error={!!errors.address?.zipCode}
                        helperText={errors.address?.zipCode?.message ?? ''}/>
                    <TextField
                        {...registerRequired('address.city')}
                        label="Stadt"
                        error={!!errors.address?.city}
                        helperText={errors.address?.city?.message ?? ''}/>
                    <TextField
                        {...registerRequired('address.street')}
                        label="Straße und Hausnummer"
                        error={!!errors.address?.street}
                        helperText={errors.address?.street?.message ?? ''}/>
                </FlexWidget>

                <FlexWidget displayName={(
                    <>
                        Besitzer
                        <IconButton color="primary" aria-label="add owner" onClick={addOwner}>
                            <PersonAddIcon/>
                        </IconButton>
                    </>
                )}>
                    {owners.map((field, index) => (
                        <Stack key={field.id} direction="row" alignItems="baseline" gap={2}>
                            <TextField
                                fullWidth
                                {...registerRequired(`owners.${index}.name`)}
                                label="Vorname, Nachname"
                                error={!!errors.owners?.[index]?.name}
                                helperText={errors.owners?.[index]?.name?.message ?? ''}/>
                            <IconButton color="primary"
                                        size="small"
                                        aria-label="remove owner"
                                        onClick={() => removeOwner(index)}>
                                <PersonRemoveIcon/>
                            </IconButton>
                        </Stack>
                    ))}
                    {!owners.length && (
                        <InfoBox>
                            Keine Besitzer hinterlegt. <Button onClick={addOwner}>Besitzer hinzufügen</Button>.
                        </InfoBox>
                    )}
                </FlexWidget>

                {!farmNumber && !vatNumber && (
                    <WarningBox>
                        Es wird eine Hofnummer oder Steuernummer benötigt.
                        {/*Alternativ kann ein Nachweisdokument (z.B. Zuchtverbandsbestätigung) angehängt werden:*/}
                        {/*<input type="file" ref={documentRef}/>*/}
                    </WarningBox>
                )}

                <Stack gap={1}>
                    <FormControl required error={!!errors.b2b}>
                        <FormControlLabel control={<Checkbox {...registerRequired('b2b')} />}
                                          label="Ich bestätige, dass ich als Unternehmer:in (kein/e Verbraucher:in) handle – als Zuchtbetrieb, Verband oder selbstständig tätige/r Züchter:in/Alpakahalter:in"/>
                        <FormHelperText>{errors.b2b?.message ?? ''}</FormHelperText>
                    </FormControl>
                    <FormControl required error={!!errors.terms}>
                        <FormControlLabel control={<Checkbox  {...registerRequired('terms')}/>}
                                          label={<>Ich habe die <a href="https://www.alpacuna.com/de/rechtliches/alpacuna-agb/">AGB</a> gelesen und akzeptiere sie</>}/>
                        <FormHelperText>{errors.terms?.message ?? ''}</FormHelperText>
                    </FormControl>
                    <FormControl required error={!!errors.privacy}>
                        <FormControlLabel control={<Checkbox {...registerRequired('privacy')}/>}
                                          label={<>Ich habe die <a href="https://www.alpacuna.com/de/rechtliches/alpacuna-datenschutzerklarung/">Datenschutzerklärung</a> zur Kenntnis genommen</>}/>
                        <FormHelperText>{errors.privacy?.message ?? ''}</FormHelperText>
                    </FormControl>
                </Stack>

                <Stack direction="row" justifyContent="right">
                    <Button variant="contained" type="submit" disabled={isPending}>Registrierung abschließen</Button>
                </Stack>
            </FlexStack>
        </form>
    );
};

export default SignupBreeder
